import { ENV } from '@/utils/env';
import { downloadFile } from '@/utils/file/utils';
import {
    CreateCustomerPayload,
    Customer,
    GetCustomerPayLoad,
    GetCustomerListPayLoad,
    ICustomerAttributes,
    UpdateCustomerPayload,
    GetCustomerListResponse,
    CreateBadgePayload,
    UpdateBadgePayload,
    DeleteBadgePayload,
    GetCustomerListForExportResponse,
} from './customer.types';
import { ApiTagTypes, innovorderApi } from '../index';
import { ApiRequest, ApiResponse } from '../request.types';

const customerApi = innovorderApi.injectEndpoints({
    endpoints: (build) => ({
        createCustomer: build.mutation<Customer, ApiRequest<CreateCustomerPayload>>({
            query: ({
                firstName,
                lastName,
                password,
                confirmPassword,
                badgeNumber,
                email,
                brandId,
                accountPaymentType,
                accountType,
                guestGroupId,
                pricingRuleId,
                studentNumber,
                role,
                token,
                departureDate,
                arrivalDate,
                phoneNumber,
                badges,
            }) => ({
                url: `/brands/${brandId}/customers`,
                method: 'POST',
                body: {
                    firstName,
                    lastName,
                    password,
                    confirmPassword,
                    badgeNumber,
                    email,
                    brandId,
                    role,
                    phone: phoneNumber,
                    badges,
                    student: {
                        accountPaymentType,
                        accountType,
                        guestGroupId,
                        pricingRuleId,
                        studentNumber,
                        accessFrom: arrivalDate,
                        accessUntil: departureDate,
                    },
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<Customer>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        getCustomerList: build.mutation<GetCustomerListResponse, ApiRequest<GetCustomerListPayLoad>>({
            query: ({
                brandId,
                getArchivedCustomers,
                getDisabledCustomers,
                selectedRestaurantId,
                emailDomainNames,
                groups,
                sections,
                subsections,
                search,
                orderBy,
                limit,
                offset,
            }) => {
                const include = ['orderStats', 'optIn', 'ewalletBalance'];

                if (getArchivedCustomers) {
                    include.push('archived');
                }

                if (getDisabledCustomers) {
                    include.push('disabled');
                }

                const params = {
                    brandId,
                    ...(selectedRestaurantId ? { restaurantIds: selectedRestaurantId } : {}),
                    ...(emailDomainNames ? { emailDomainNames } : {}),
                    ...(groups ? { groups } : {}),
                    ...(sections ? { sections } : {}),
                    ...(subsections ? { subsections } : {}),
                    ...(search ? { search } : {}),
                    ...(orderBy ? { orderBy } : {}),
                    limit,
                    offset,
                    include: include.join(','),
                };

                return {
                    url: '/customers',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response: ApiResponse<GetCustomerListResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        getCustomerByCustomerId: build.query<ICustomerAttributes, ApiRequest<GetCustomerPayLoad>>({
            query: ({ customerId, token }) => ({
                url: `/customers/${customerId}?unscoped=true`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.Customer],
            transformResponse: (response: ApiResponse<ICustomerAttributes>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        updateCustomer: build.mutation<ICustomerAttributes, ApiRequest<UpdateCustomerPayload>>({
            query: (updateCustomerPayloadWithToken) => {
                const { token, customerId, ...updateCustomerPayload } = updateCustomerPayloadWithToken;
                return {
                    url: `customers/${customerId}`,
                    method: 'PUT',
                    body: {
                        ...updateCustomerPayload,
                    },
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                };
            },
            transformResponse: (response: ApiResponse<ICustomerAttributes>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        addBadge: build.mutation<ICustomerAttributes, ApiRequest<CreateBadgePayload>>({
            query: (badgePayloadWithToken) => {
                const { token, customerId, ...badgePayload } = badgePayloadWithToken;
                return {
                    url: `customers/${customerId}/badges`,
                    method: 'POST',
                    body: {
                        ...badgePayload,
                    },
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                };
            },
            transformResponse: (response: ApiResponse<ICustomerAttributes>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        updateBadge: build.mutation<ICustomerAttributes, ApiRequest<UpdateBadgePayload>>({
            query: (badgePayloadWithToken) => {
                const { token, customerId, badgeId, ...badgePayload } = badgePayloadWithToken;
                return {
                    url: `customers/${customerId}/badges/${badgeId}`,
                    method: 'PUT',
                    body: {
                        ...badgePayload,
                    },
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                };
            },
            transformResponse: (response: ApiResponse<ICustomerAttributes>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        deleteBadge: build.mutation<ICustomerAttributes, ApiRequest<DeleteBadgePayload>>({
            query: ({ token, customerId, badgeId }) => {
                return {
                    url: `customers/${customerId}/badges/${badgeId}`,
                    method: 'DELETE',
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                };
            },
            transformResponse: (response: ApiResponse<ICustomerAttributes>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        downloadCustomerTicket: build.mutation<null, ApiRequest<{ orderId: string }>>({
            queryFn: async ({ orderId, token }) => {
                try {
                    const response = await fetch(`${ENV.API_URL}/orders/tickets/${orderId}`, {
                        method: 'GET',
                        headers: {
                            authorization: `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) {
                        return { error: { status: response.status, data: await response.text() } };
                    }

                    const contentDisposition = response.headers.get('content-disposition');
                    const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/);
                    const filename = filenameMatch?.[1] ?? `ticket-${orderId}.pdf`;

                    const blob = await response.blob();
                    const arrayBuffer = await blob.arrayBuffer();
                    downloadFile(filename, arrayBuffer, 'application/pdf');

                    return { data: null };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR' as const, error: String(error) } };
                }
            },
        }),

        getCustomerListForExport: build.query<GetCustomerListForExportResponse, ApiRequest<GetCustomerListPayLoad>>({
            query: ({
                brandId,
                getArchivedCustomers,
                getDisabledCustomers,
                selectedRestaurantId,
                emailDomainNames,
                groups,
                sections,
                subsections,
                search,
                orderBy,
            }) => {
                // Match V1 backoffice exactly: include=orderStats,optIn,badges (no ewalletBalance, no pagination)
                const include = ['orderStats', 'optIn', 'badges'];

                if (getArchivedCustomers) {
                    include.push('archived');
                }

                if (getDisabledCustomers) {
                    include.push('disabled');
                }

                const params = {
                    brandId,
                    ...(selectedRestaurantId ? { restaurantIds: selectedRestaurantId } : {}),
                    ...(emailDomainNames ? { emailDomainNames } : {}),
                    ...(groups ? { groups } : {}),
                    ...(sections ? { sections } : {}),
                    ...(subsections ? { subsections } : {}),
                    ...(search ? { search } : {}),
                    ...(orderBy ? { orderBy } : {}),
                    include: include.join(','),
                };

                return {
                    url: '/customers',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response: ApiResponse<GetCustomerListForExportResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        // Full export with ewalletBalance for Solde column (V1 "Exporter" button)
        getCustomerListForFullExport: build.query<GetCustomerListForExportResponse, ApiRequest<{ brandId: number }>>({
            query: ({ brandId }) => {
                // Match V1: include=orderStats,optIn,ewalletBalance
                const params = {
                    brandId,
                    include: 'orderStats,optIn,ewalletBalance',
                };

                return {
                    url: '/customers',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response: ApiResponse<GetCustomerListForExportResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
    }),
});

export const {
    useGetCustomerListMutation,
    useCreateCustomerMutation,
    useGetCustomerByCustomerIdQuery,
    useUpdateCustomerMutation,
    useAddBadgeMutation,
    useUpdateBadgeMutation,
    useDeleteBadgeMutation,
    useDownloadCustomerTicketMutation,
    useLazyGetCustomerListForExportQuery,
    useGetCustomerListForExportQuery,
    useLazyGetCustomerListForFullExportQuery,
} = customerApi;
